import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import apiService from '../../services/apiService';
import API_BASE_URL from '../../config/api';
import {
    Plus, Trash2, Edit3, Save, X, GripVertical, Eye, EyeOff,
    Image as ImageIcon, ChevronDown, ChevronUp, Sparkles, PackageOpen
} from 'lucide-react';

// Preset color options for product cards
const COLOR_PRESETS = [
    { label: 'Green / Emerald', from: 'green-500', to: 'emerald-500', bgFrom: 'green-50', bgTo: 'emerald-50' },
    { label: 'Blue / Cyan', from: 'blue-500', to: 'cyan-500', bgFrom: 'blue-50', bgTo: 'cyan-50' },
    { label: 'Purple / Pink', from: 'purple-500', to: 'pink-500', bgFrom: 'purple-50', bgTo: 'pink-50' },
    { label: 'Rose / Orange', from: 'rose-500', to: 'orange-500', bgFrom: 'rose-50', bgTo: 'orange-50' },
    { label: 'Lime / Green', from: 'lime-500', to: 'green-500', bgFrom: 'lime-50', bgTo: 'green-50' },
    { label: 'Amber / Yellow', from: 'amber-500', to: 'yellow-500', bgFrom: 'amber-50', bgTo: 'yellow-50' },
    { label: 'Pink / Fuchsia', from: 'pink-500', to: 'fuchsia-500', bgFrom: 'pink-50', bgTo: 'fuchsia-50' },
    { label: 'Indigo / Blue', from: 'indigo-500', to: 'blue-500', bgFrom: 'indigo-50', bgTo: 'blue-50' },
    { label: 'Teal / Cyan', from: 'teal-500', to: 'cyan-500', bgFrom: 'teal-50', bgTo: 'cyan-50' },
    { label: 'Orange / Red', from: 'orange-500', to: 'red-500', bgFrom: 'orange-50', bgTo: 'red-50' },
];

const ICON_OPTIONS = ['🌿', '💧', '🧲', '✨', '🌾', '🚜', '💄', '💊', '🏥', '🧴', '🌸', '🍀', '🔬', '🧪', '🌻', '🍃', '🥗', '🌽', '🧬', '🎋'];

const emptyProduct = {
    name: '',
    category: '',
    tagline: '',
    description: '',
    image_url: '',
    icon: '🌿',
    color_from: 'green-500',
    color_to: 'emerald-500',
    bg_from: 'green-50',
    bg_to: 'emerald-50',
    benefits: [''],
    sort_order: 0,
    is_active: true,
};

const LandingProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [form, setForm] = useState({ ...emptyProduct });
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [uploadingImage, setUploadingImage] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [expandedCard, setExpandedCard] = useState(null);

    const fetchProducts = useCallback(async () => {
        try {
            const data = await apiService.getLandingProducts(true);
            if (data.success) {
                setProducts(data.products || []);
            }
        } catch (error) {
            console.error('Failed to fetch landing products:', error);
            setMessage({ type: 'error', text: 'Failed to fetch products' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingImage(true);
        try {
            const token = localStorage.getItem('authToken');
            const formData = new FormData();
            formData.append('image', file);
            formData.append('type', 'landing_product');

            const response = await fetch(`${API_BASE_URL}/admin/upload_image.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await response.json();
            if (data.success) {
                setForm(prev => ({ ...prev, image_url: data.image_url }));
                setMessage({ type: 'success', text: 'Image uploaded successfully' });
            } else {
                setMessage({ type: 'error', text: data.message || 'Image upload failed' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Image upload failed' });
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            setMessage({ type: 'error', text: 'Product name is required' });
            return;
        }

        setSaving(true);
        try {
            const isEditing = editingProduct !== null;
            
            // Clean up benefits - remove empty strings
            const cleanedBenefits = form.benefits.filter(b => b.trim() !== '');

            const productData = {
                ...form,
                benefits: cleanedBenefits,
                ...(isEditing ? { id: editingProduct.id } : {})
            };

            const data = isEditing 
                ? await apiService.updateLandingProduct(productData)
                : await apiService.createLandingProduct(productData);

            if (data.success) {
                setMessage({ type: 'success', text: data.message });
                setShowForm(false);
                setEditingProduct(null);
                setForm({ ...emptyProduct });
                fetchProducts();
            } else {
                setMessage({ type: 'error', text: data.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to save product' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const data = await apiService.deleteLandingProduct(id);

            if (data.success) {
                setMessage({ type: 'success', text: 'Product deleted successfully' });
                setDeleteConfirm(null);
                fetchProducts();
            } else {
                setMessage({ type: 'error', text: data.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to delete product' });
        }
    };

    const startEdit = (product) => {
        setEditingProduct(product);
        setForm({
            name: product.name || '',
            category: product.category || '',
            tagline: product.tagline || '',
            description: product.description || '',
            image_url: product.image_url || '',
            icon: product.icon || '🌿',
            color_from: product.color_from || 'green-500',
            color_to: product.color_to || 'emerald-500',
            bg_from: product.bg_from || 'green-50',
            bg_to: product.bg_to || 'emerald-50',
            benefits: (product.benefits && product.benefits.length > 0) ? [...product.benefits] : [''],
            sort_order: product.sort_order || 0,
            is_active: Number(product.is_active) === 1,
        });
        setShowForm(true);
    };

    const addBenefit = () => {
        setForm(prev => ({ ...prev, benefits: [...prev.benefits, ''] }));
    };

    const removeBenefit = (index) => {
        setForm(prev => ({
            ...prev,
            benefits: prev.benefits.filter((_, i) => i !== index)
        }));
    };

    const updateBenefit = (index, value) => {
        setForm(prev => ({
            ...prev,
            benefits: prev.benefits.map((b, i) => i === index ? value : b)
        }));
    };

    const applyColorPreset = (preset) => {
        setForm(prev => ({
            ...prev,
            color_from: preset.from,
            color_to: preset.to,
            bg_from: preset.bgFrom,
            bg_to: preset.bgTo,
        }));
    };

    // Clear message after 4 seconds
    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => setMessage({ type: '', text: '' }), 4000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-purple-900 via-indigo-800 to-purple-900 bg-clip-text text-transparent tracking-tight flex items-center gap-3">
                            <Sparkles className="text-purple-600" size={28} />
                            Landing Page Products
                        </h1>
                        <p className="text-gray-500 mt-1 text-sm font-medium">
                            Manage products displayed on the public landing page showcase
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingProduct(null);
                            setForm({ ...emptyProduct });
                            setShowForm(true);
                        }}
                        className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-sm font-bold rounded-xl shadow-lg shadow-purple-200 transition-all active:scale-95"
                    >
                        <Plus size={18} />
                        Add Product
                    </button>
                </div>

                {/* Toast Message */}
                {message.text && (
                    <div className={`p-4 rounded-xl text-sm font-bold flex items-center justify-between ${
                        message.type === 'success' 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                        <span>{message.text}</span>
                        <button onClick={() => setMessage({ type: '', text: '' })} className="p-1 hover:bg-white/50 rounded-full">
                            <X size={14} />
                        </button>
                    </div>
                )}

                {/* Products Grid */}
                {products.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
                        <PackageOpen size={64} className="mx-auto text-gray-200 mb-4" />
                        <h3 className="text-lg font-bold text-gray-400 mb-2">No Landing Products Yet</h3>
                        <p className="text-gray-400 text-sm mb-6">Add products that will be showcased on the public landing page</p>
                        <button
                            onClick={() => {
                                setEditingProduct(null);
                                setForm({ ...emptyProduct });
                                setShowForm(true);
                            }}
                            className="px-6 py-3 bg-purple-600 text-white rounded-xl font-bold text-sm hover:bg-purple-700 transition inline-flex items-center gap-2"
                        >
                            <Plus size={16} /> Add First Product
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                        {products.map((product, index) => (
                            <div
                                key={product.id}
                                className={`bg-white rounded-2xl border shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group ${
                                    Number(product.is_active) === 1 ? 'border-gray-100' : 'border-red-200 opacity-70'
                                }`}
                            >
                                {/* Product Image */}
                                <div className="relative h-44 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center overflow-hidden">
                                    {product.image_url ? (
                                        <img
                                            src={product.image_url}
                                            alt={product.name}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    ) : (
                                        <div className="text-6xl">{product.icon}</div>
                                    )}
                                    
                                    {/* Status Badge */}
                                    <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider shadow-sm ${
                                        Number(product.is_active) === 1
                                            ? 'bg-green-500 text-white'
                                            : 'bg-red-500 text-white'
                                    }`}>
                                        {Number(product.is_active) === 1 ? 'Active' : 'Hidden'}
                                    </div>

                                    {/* Order Badge */}
                                    <div className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center text-xs font-black text-gray-700 shadow-sm">
                                        #{product.sort_order || index + 1}
                                    </div>

                                    {/* Category Badge */}
                                    {product.category && (
                                        <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-white/90 backdrop-blur-sm rounded-lg text-[10px] font-bold text-gray-700 shadow-sm">
                                            {product.category}
                                        </div>
                                    )}
                                </div>

                                {/* Product Info */}
                                <div className="p-4">
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                        <h3 className="font-black text-gray-900 text-sm leading-tight">{product.name}</h3>
                                        <span className="text-xl flex-shrink-0">{product.icon}</span>
                                    </div>
                                    {product.tagline && (
                                        <p className="text-[11px] text-purple-600 font-semibold mb-2">{product.tagline}</p>
                                    )}

                                    {/* Expandable details */}
                                    <button
                                        onClick={() => setExpandedCard(expandedCard === product.id ? null : product.id)}
                                        className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1 hover:text-purple-600 transition mb-2"
                                    >
                                        {expandedCard === product.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                        {expandedCard === product.id ? 'Less' : 'Details'}
                                    </button>

                                    {expandedCard === product.id && (
                                        <div className="mb-3 space-y-2">
                                            {product.description && (
                                                <p className="text-xs text-gray-500 leading-relaxed">{product.description.substring(0, 120)}...</p>
                                            )}
                                            {product.benefits && product.benefits.length > 0 && (
                                                <div className="space-y-1">
                                                    {product.benefits.slice(0, 3).map((b, i) => (
                                                        <div key={i} className="flex items-center gap-1.5 text-[11px] text-gray-600">
                                                            <span className="text-green-500">✓</span> {b}
                                                        </div>
                                                    ))}
                                                    {product.benefits.length > 3 && (
                                                        <span className="text-[10px] text-gray-400 font-bold">+{product.benefits.length - 3} more</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                                        <button
                                            onClick={() => startEdit(product)}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-purple-50 text-purple-700 rounded-lg text-xs font-bold hover:bg-purple-100 transition"
                                        >
                                            <Edit3 size={13} /> Edit
                                        </button>
                                        {deleteConfirm === product.id ? (
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    onClick={() => handleDelete(product.id)}
                                                    className="px-3 py-2 bg-red-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-red-700 transition"
                                                >
                                                    Confirm
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirm(null)}
                                                    className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-gray-200 transition"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setDeleteConfirm(product.id)}
                                                className="flex items-center justify-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 rounded-lg text-xs font-bold hover:bg-red-100 transition"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Add/Edit Form Modal */}
                {showForm && (
                    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-8 bg-black/50 backdrop-blur-sm overflow-y-auto" onClick={() => { setShowForm(false); setEditingProduct(null); }}>
                        <div 
                            className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full relative my-4"
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="sticky top-0 z-10 bg-gradient-to-r from-purple-700 to-indigo-700 px-6 py-5 flex items-center justify-between rounded-t-3xl">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-purple-200 mb-1">Landing Page Showcase</p>
                                    <h3 className="text-lg font-black text-white">
                                        {editingProduct ? 'Edit Product' : 'Add New Product'}
                                    </h3>
                                </div>
                                <button
                                    onClick={() => { setShowForm(false); setEditingProduct(null); }}
                                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
                                {/* Basic Info */}
                                <div className="space-y-4">
                                    <h4 className="text-[11px] font-black uppercase tracking-widest text-gray-400 border-b border-gray-100 pb-2">Basic Information</h4>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-black uppercase tracking-widest text-gray-500">Product Name *</label>
                                            <input
                                                type="text"
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                                                placeholder="e.g. Herbal Health Supplements"
                                                required
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-black uppercase tracking-widest text-gray-500">Category</label>
                                            <input
                                                type="text"
                                                value={form.category}
                                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                                                placeholder="e.g. Ayurvedic, Agriculture"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black uppercase tracking-widest text-gray-500">Tagline</label>
                                        <input
                                            type="text"
                                            value={form.tagline}
                                            onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                                            placeholder="Short catchy tagline"
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black uppercase tracking-widest text-gray-500">Description</label>
                                        <textarea
                                            value={form.description}
                                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition resize-none"
                                            rows="3"
                                            placeholder="Detailed product description..."
                                        />
                                    </div>
                                </div>

                                {/* Image Upload */}
                                <div className="space-y-3">
                                    <h4 className="text-[11px] font-black uppercase tracking-widest text-gray-400 border-b border-gray-100 pb-2">Product Image</h4>
                                    
                                    <div className="flex items-start gap-4">
                                        {/* Preview */}
                                        <div className="w-32 h-32 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center overflow-hidden flex-shrink-0">
                                            {form.image_url ? (
                                                <img src={form.image_url} alt="Preview" className="w-full h-full object-cover rounded-xl" />
                                            ) : (
                                                <ImageIcon size={32} className="text-gray-300" />
                                            )}
                                        </div>
                                        
                                        <div className="flex-1 space-y-2">
                                            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 bg-purple-50 text-purple-700 rounded-xl text-xs font-bold hover:bg-purple-100 transition border border-purple-100">
                                                <ImageIcon size={14} />
                                                {uploadingImage ? 'Uploading...' : 'Upload Image'}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageUpload}
                                                    className="hidden"
                                                    disabled={uploadingImage}
                                                />
                                            </label>
                                            <p className="text-[10px] text-gray-400 font-medium">JPG, PNG, WebP. Max 5MB.</p>
                                            
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Or paste image URL</label>
                                                <input
                                                    type="text"
                                                    value={form.image_url}
                                                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                                                    className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs font-medium focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                                                    placeholder="https://..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Appearance */}
                                <div className="space-y-3">
                                    <h4 className="text-[11px] font-black uppercase tracking-widest text-gray-400 border-b border-gray-100 pb-2">Appearance</h4>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        {/* Icon Picker */}
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-black uppercase tracking-widest text-gray-500">Icon</label>
                                            <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 rounded-xl border border-gray-200 max-h-24 overflow-y-auto">
                                                {ICON_OPTIONS.map((icon, i) => (
                                                    <button
                                                        type="button"
                                                        key={i}
                                                        onClick={() => setForm({ ...form, icon })}
                                                        className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg transition ${
                                                            form.icon === icon ? 'bg-purple-200 ring-2 ring-purple-500' : 'bg-white hover:bg-gray-100'
                                                        }`}
                                                    >
                                                        {icon}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Sort Order & Status */}
                                        <div className="space-y-3">
                                            <div className="space-y-1.5">
                                                <label className="text-[11px] font-black uppercase tracking-widest text-gray-500">Sort Order</label>
                                                <input
                                                    type="number"
                                                    value={form.sort_order}
                                                    onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                                                    min="0"
                                                />
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setForm({ ...form, is_active: !form.is_active })}
                                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition border ${
                                                        form.is_active
                                                            ? 'bg-green-50 text-green-700 border-green-200'
                                                            : 'bg-red-50 text-red-700 border-red-200'
                                                    }`}
                                                >
                                                    {form.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                                                    {form.is_active ? 'Visible' : 'Hidden'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Color Presets */}
                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black uppercase tracking-widest text-gray-500">Card Color Theme</label>
                                        <div className="flex flex-wrap gap-2">
                                            {COLOR_PRESETS.map((preset, i) => {
                                                const isSelected = form.color_from === preset.from && form.color_to === preset.to;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={i}
                                                        onClick={() => applyColorPreset(preset)}
                                                        className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition border ${
                                                            isSelected
                                                                ? 'bg-purple-600 text-white border-purple-600 shadow-md'
                                                                : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-purple-300'
                                                        }`}
                                                    >
                                                        {preset.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {/* Benefits */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                                        <h4 className="text-[11px] font-black uppercase tracking-widest text-gray-400">Key Benefits</h4>
                                        <button
                                            type="button"
                                            onClick={addBenefit}
                                            className="text-[10px] font-bold text-purple-600 hover:text-purple-700 flex items-center gap-1 transition"
                                        >
                                            <Plus size={12} /> Add Benefit
                                        </button>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        {form.benefits.map((benefit, index) => (
                                            <div key={index} className="flex items-center gap-2">
                                                <GripVertical size={14} className="text-gray-300 flex-shrink-0" />
                                                <input
                                                    type="text"
                                                    value={benefit}
                                                    onChange={(e) => updateBenefit(index, e.target.value)}
                                                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-xs font-semibold focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                                                    placeholder={`Benefit ${index + 1}`}
                                                />
                                                {form.benefits.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeBenefit(index)}
                                                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Submit Buttons */}
                                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                                    <button
                                        type="button"
                                        onClick={() => { setShowForm(false); setEditingProduct(null); }}
                                        className="px-6 py-3 bg-gray-100 text-gray-600 rounded-xl text-sm font-bold hover:bg-gray-200 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl text-sm font-bold hover:from-purple-700 hover:to-indigo-700 transition shadow-lg shadow-purple-200 disabled:opacity-50"
                                    >
                                        <Save size={16} />
                                        {saving ? 'Saving...' : (editingProduct ? 'Update Product' : 'Create Product')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default LandingProducts;
